import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { widget } from "~/charting_library";
import { WidgetCreatorClass } from "../../../utils/classes/widgetCreatorClass";
import { createChartConfig } from "../../../config/createChartConfig";
import { processChartData, renderTradeBoundariesPoints } from "./helper";
import { getPairDataConfig } from "../../../utils/helpers/tools/getPairDataConfig";
import "../../index.css";
import {
  createChartfavorites, createChartFavoritesSettingsAdapter
} from "~/components/child/tradingViewMainChart/utils/creators/createChartFavorites";

export const TVChartContainer = React.memo(
  ({
    tradeBoundariesPoints,
    pairDetail,
    initInterval,
    baseDateType,
    techChartData,
    setTechChartData,
    theme,
    setDataHasChanged: setEditDataHasChanged,
    blurPointInputTrigger,
    step,
    mainChartFavorites,
    setMainChartFavorites,
    hasFavoriteChartDataChanged
  }) => {
    const chartContainerRef = useRef();
    const createWidgetRef = useRef(null);
    const tvWidgetRef = useRef(null);

    const [isChartReady, setIsChartReady] = useState(false);
    const [tradeBoundariesWidgetIds, setTradeBoundariesWidgetIds] = useState([]);

    const { baseChartConfig, specificConfigs } = createChartConfig("technical", {
      timezone: "Asia/Tehran",
    });

    const { interval, finalChartData } = processChartData(initInterval, techChartData);
    const { pair, dataFeed: datafeedUrl } = getPairDataConfig(pairDetail, "camelCase");

    const tradeBoundariesPointsData = [
      { points: tradeBoundariesPoints?.ep, type: "ep" },
      { points: tradeBoundariesPoints?.sl, type: "sl" },
      { points: tradeBoundariesPoints?.tp, type: "tp" },
    ];
    useEffect(() => {
      const createWidget = createWidgetRef.current;
      const tvWidget = tvWidgetRef.current;
      if (isChartReady && createWidget && tvWidget) {
        
        setTradeBoundariesWidgetIds([]);
        tradeBoundariesWidgetIds.forEach((id) => {
          tvWidget.activeChart().removeEntity(id, {
            disableUndo: true,
          });
        });

        tradeBoundariesPointsData.forEach(({ points, type }) =>
          renderTradeBoundariesPoints(
            points,
            type,
            baseDateType,
            interval,
            createWidget,
            setTradeBoundariesWidgetIds
          )
        );
      }
    }, [
      tradeBoundariesPoints?.ep.length,
      tradeBoundariesPoints?.sl.length,
      tradeBoundariesPoints?.tp.length,
      blurPointInputTrigger,
      isChartReady,
      step,
    ]);

    useEffect(() => {
      const widgetOptions = {
        ...baseChartConfig?.defaultProps,
        container: chartContainerRef.current,
        datafeed: baseChartConfig?.createDatafeed(datafeedUrl),
        disabled_features: specificConfigs?.create?.disabledFeatures,
        enabled_features: specificConfigs?.create?.enabledFeatures,
        theme: theme,
        symbol: pair,
        interval: interval,
        saved_data: finalChartData,

        favorites: createChartfavorites(mainChartFavorites),
        settings_adapter: createChartFavoritesSettingsAdapter(mainChartFavorites, setMainChartFavorites),
      };

      const tvWidget = new widget(widgetOptions);
      const createWidget = new WidgetCreatorClass(tvWidget);
      tvWidgetRef.current = tvWidget;
      createWidgetRef.current = createWidget;

      tvWidget.onChartReady(() => {
        setIsChartReady(true);
        tvWidget.headerReady().then(() => {
          tvWidget.save((data) => {
            setTechChartData(data);
          });
          tvWidget.subscribe("undo_redo_state_changed", () => {
            tvWidget.save((data) => {
              setTechChartData(data);
              setEditDataHasChanged(true);
            });
          });
        });
      });

      return () => {
        if (tvWidgetRef.current) {
          tvWidgetRef.current.remove();
          tvWidgetRef.current = null;
          createWidgetRef.current = null;
          setIsChartReady(false);
          hasFavoriteChartDataChanged()
        }
      };
    }, [theme, interval, pair]);

    return <div ref={chartContainerRef} className={"TVChartContainer"} />;
  }
);

TVChartContainer.propTypes = {
  tradeBoundariesPoints: PropTypes.object.isRequired,
  pairDetail: PropTypes.object.isRequired,
  initInterval: PropTypes.string.isRequired,
  baseDateType: PropTypes.string.isRequired,
  step: PropTypes.string.isRequired,
  techChartData: PropTypes.object.isRequired,
  setTechChartData: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  setDataHasChanged: PropTypes.func.isRequired,
  blurPointInputTrigger: PropTypes.number.isRequired,
  mainChartFavorites: PropTypes.array.isRequired,
  setMainChartFavorites: PropTypes.func.isRequired,
  hasFavoriteChartDataChanged: PropTypes.func.isRequired,
};
